import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import { Button } from './ui/Button';
export function PricingSection() {
  return <section id="pricing" className="py-24 bg-background relative overflow-hidden" data-id="element-118">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-neon-purple/5 rounded-full blur-[120px] pointer-events-none" data-id="element-119" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-120">
        <div className="text-center mb-16" data-id="element-121">
          <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6" data-id="element-122">
            BACKSTAGE <span className="text-neon-magenta" data-id="element-123">PASS</span>
          </h2>
          <p className="text-xl text-gray-400" data-id="element-124">Choose your level of access.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-id="element-125">
          {/* Basic Tier */}
          <div className="bg-surface p-8 rounded-2xl border border-white/10 opacity-80 hover:opacity-100 transition-opacity" data-id="element-126">
            <h3 className="text-2xl font-bold text-white mb-2 font-display" data-id="element-127">
              Opener
            </h3>
            <div className="text-4xl font-bold text-white mb-6" data-id="element-128">
              €0<span className="text-lg text-gray-500 font-normal" data-id="element-129">/mo</span>
            </div>
            <p className="text-gray-400 mb-8 text-sm" data-id="element-130">
              For casual players just starting their journey.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-131">
              <li className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-132">
                <Check className="w-4 h-4 text-neon-cyan" data-id="element-133" /> 5 Free Tracks /
                Month
              </li>
              <li className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-134">
                <Check className="w-4 h-4 text-neon-cyan" data-id="element-135" /> Standard Audio
                Quality
              </li>
              <li className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-136">
                <Check className="w-4 h-4 text-neon-cyan" data-id="element-137" /> Global Leaderboards
              </li>
            </ul>
            <Button variant="outline" className="w-full" data-id="element-138">
              Start Free
            </Button>
          </div>

          {/* Pro Tier */}
          <motion.div whileHover={{
          y: -10
        }} className="bg-surface p-8 rounded-2xl border-2 border-neon-magenta relative shadow-[0_0_30px_rgba(255,45,123,0.15)] z-10" data-id="element-139">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-neon-magenta text-white px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest flex items-center gap-1" data-id="element-140">
              <Star className="w-3 h-3 fill-current" data-id="element-141" /> Headliner
            </div>
            <h3 className="text-2xl font-bold text-white mb-2 font-display" data-id="element-142">
              Headliner
            </h3>
            <div className="text-5xl font-bold text-white mb-6" data-id="element-143">
              €12<span className="text-lg text-gray-500 font-normal" data-id="element-144">/mo</span>
            </div>
            <p className="text-gray-400 mb-8 text-sm" data-id="element-145">
              The full experience for serious rhythm gamers.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-146">
              <li className="flex items-center gap-3 text-white font-medium" data-id="element-147">
                <Check className="w-5 h-5 text-neon-magenta" data-id="element-148" /> Unlimited Tracks
              </li>
              <li className="flex items-center gap-3 text-white font-medium" data-id="element-149">
                <Check className="w-5 h-5 text-neon-magenta" data-id="element-150" /> Lossless FLAC
                Audio
              </li>
              <li className="flex items-center gap-3 text-white font-medium" data-id="element-151">
                <Check className="w-5 h-5 text-neon-magenta" data-id="element-152" /> Early Access to
                Drops
              </li>
              <li className="flex items-center gap-3 text-white font-medium" data-id="element-153">
                <Check className="w-5 h-5 text-neon-magenta" data-id="element-154" /> Custom Vinyl
                Skins
              </li>
            </ul>
            <Button variant="secondary" className="w-full" glow data-id="element-155">
              Go Headliner
            </Button>
          </motion.div>

          {/* Enterprise Tier */}
          <div className="bg-surface p-8 rounded-2xl border border-white/10 opacity-80 hover:opacity-100 transition-opacity" data-id="element-156">
            <h3 className="text-2xl font-bold text-white mb-2 font-display" data-id="element-157">
              Label
            </h3>
            <div className="text-4xl font-bold text-white mb-6" data-id="element-158">
              €29<span className="text-lg text-gray-500 font-normal" data-id="element-159">/mo</span>
            </div>
            <p className="text-gray-400 mb-8 text-sm" data-id="element-160">
              For creators and streamers.
            </p>
            <ul className="space-y-4 mb-8" data-id="element-161">
              <li className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-162">
                <Check className="w-4 h-4 text-neon-cyan" data-id="element-163" /> Commercial License
              </li>
              <li className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-164">
                <Check className="w-4 h-4 text-neon-cyan" data-id="element-165" /> Stem Separation
                Tools
              </li>
              <li className="flex items-center gap-3 text-gray-300 text-sm" data-id="element-166">
                <Check className="w-4 h-4 text-neon-cyan" data-id="element-167" /> Stream Overlay Pack
              </li>
            </ul>
            <Button variant="outline" className="w-full" data-id="element-168">
              Join Label
            </Button>
          </div>
        </div>
      </div>
    </section>;
}